import React, { useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Package } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin Customers tab — list registered customers with stats, search, and
 * a per-customer Orders modal.
 */
export const CustomersTab = ({
  customers,
  customerSearch,
  setCustomerSearch,
  selectedCustomer,
  setSelectedCustomer,
  customerOrders,
  setCustomerOrders,
  fetchCustomerOrders,
  fetchCustomers,
  formatPrice,
  setSuccess,
  setError,
}) => {
  const totalOrders = customers.reduce((sum, c) => sum + (c.order_count || 0), 0);
  const totalSpent = customers.reduce((sum, c) => sum + (c.total_spent || 0), 0);
  const withOrders = customers.filter((c) => c.order_count > 0).length;

  // Coupon history for the selected customer (fetched when the modal opens)
  const [customerCoupons, setCustomerCoupons] = React.useState(null);
  React.useEffect(() => {
    if (!selectedCustomer) {
      setCustomerCoupons(null);
      return;
    }
    axios
      .get(`${API}/customers/${selectedCustomer.id}/coupons`)
      .then((r) => setCustomerCoupons(r.data || []))
      .catch(() => setCustomerCoupons([]));
  }, [selectedCustomer]);

  // Memoize the filtered list so we don't recompute on every keystroke unrelated to it
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    const s = customerSearch.toLowerCase();
    return customers.filter(
      (c) =>
        (c.full_name || c.username || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.phone || "").includes(s)
    );
  }, [customers, customerSearch]);


  return (
    <>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white" data-testid="customers-title">
              Gestione Clienti
            </h2>
            <p className="text-gray-400">Totale clienti registrati: {customers.length}</p>
          </div>
        </div>
      </div>

      {/* Customer Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{customers.length}</p>
            <p className="text-sm text-gray-500">Clienti Registrati</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{withOrders}</p>
            <p className="text-sm text-gray-500">Con Ordini</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{totalOrders}</p>
            <p className="text-sm text-gray-500">Ordini Totali</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{formatPrice(totalSpent)}</p>
            <p className="text-sm text-gray-500">Fatturato Clienti</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={customerSearch}
          onChange={(e) => setCustomerSearch(e.target.value)}
          placeholder="Cerca per nome, email o telefono..."
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="customer-search-input"
        />
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Package className="w-24 h-24 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-gray-200 mb-2">Nessun cliente registrato</h3>
          <p className="text-gray-400">I clienti che creano un account appariranno qui</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-3 md:p-6">
            {/* Mobile card view (visible <md) */}
            <div className="md:hidden space-y-3" data-testid="customers-cards-mobile">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="bg-white rounded-lg border border-gray-200 p-3"
                  data-testid={`customer-card-${customer.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {customer.full_name || customer.username}
                      </p>
                      <p className="text-xs text-gray-600 truncate">{customer.email}</p>
                      {customer.phone && (
                        <p className="text-xs text-gray-500 mt-0.5">📱 {customer.phone}</p>
                      )}
                      {(customer.address || customer.default_address) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          📍 {customer.address || customer.default_address}
                          {(customer.city || customer.default_city) && (
                            <>, {customer.postal_code || customer.default_zip} {customer.city || customer.default_city}</>
                          )}
                          {(customer.country || customer.default_country) && (
                            <> ({customer.country || customer.default_country})</>
                          )}
                        </p>
                      )}
                      {customer.codice_fiscale && (
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">
                          🆔 CF: {customer.codice_fiscale}
                        </p>
                      )}
                    </div>
                    <span
                      className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                        customer.order_count > 0
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {customer.order_count || 0} ordini
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-600 mt-2 pt-2 border-t border-gray-100">
                    <span>
                      Registrato:{" "}
                      <span className="text-gray-800">
                        {customer.created_at
                          ? new Date(customer.created_at).toLocaleDateString("it-IT")
                          : "-"}
                      </span>
                    </span>
                    <span className="font-semibold text-gray-900">
                      {formatPrice(customer.total_spent || 0)}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setSelectedCustomer(customer);
                        fetchCustomerOrders(customer.id);
                      }}
                      data-testid={`view-orders-btn-mobile-${customer.id}`}
                    >
                      📦 Vedi Ordini
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (
                          window.confirm(
                            `Eliminare il cliente ${customer.full_name || customer.username}?`
                          )
                        ) {
                          try {
                            await axios.delete(`${API}/customers/${customer.id}`);
                            fetchCustomers();
                            setSuccess("Cliente eliminato");
                            setTimeout(() => setSuccess(""), 3000);
                          } catch (err) {
                            setError("Errore eliminazione");
                            setTimeout(() => setError(""), 3000);
                          }
                        }
                      }}
                      data-testid={`delete-btn-mobile-${customer.id}`}
                    >
                      🗑️
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table view (visible md+) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" data-testid="customers-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Indirizzo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CF</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registrato il</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordini</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Speso</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCustomers.map((customer) => {
                    const addr = customer.address || customer.default_address || "";
                    const city = customer.city || customer.default_city || "";
                    const zip_ = customer.postal_code || customer.default_zip || "";
                    const country = customer.country || customer.default_country || "";
                    return (
                      <tr
                        key={customer.id}
                        className="hover:bg-gray-50"
                        data-testid={`customer-row-${customer.id}`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {customer.full_name || customer.username}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[190px]">
                          <div className="truncate" title={customer.email}>{customer.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{customer.phone || "-"}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 max-w-[240px]">
                          {addr ? (
                            <div data-testid={`customer-address-${customer.id}`}>
                              <div className="truncate" title={addr}>{addr}</div>
                              <div className="text-gray-500">
                                {zip_ && <span>{zip_} </span>}
                                {city}
                                {country && <span className="ml-1 text-gray-400">({country})</span>}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 font-mono" data-testid={`customer-cf-${customer.id}`}>
                          {customer.codice_fiscale || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {customer.created_at
                            ? new Date(customer.created_at).toLocaleDateString("it-IT")
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`font-semibold ${customer.order_count > 0 ? "text-green-600" : "text-gray-400"}`}
                          >
                            {customer.order_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {formatPrice(customer.total_spent || 0)}
                        </td>
                        <td className="px-2 py-3 text-sm whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="px-2"
                              title="Vedi ordini e coupon del cliente"
                              onClick={() => {
                                setSelectedCustomer(customer);
                                fetchCustomerOrders(customer.id);
                              }}
                              data-testid={`view-orders-btn-${customer.id}`}
                            >
                              📦
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                              title="Invia un coupon % personale via email a questo cliente"
                            onClick={async () => {
                              const pctRaw = window.prompt(
                                `Percentuale di sconto da inviare a ${customer.email}? (1-50)`,
                                "10"
                              );
                              if (pctRaw === null) return;
                              const pct = parseInt(pctRaw, 10);
                              if (!pct || pct < 1 || pct > 50) {
                                setError("Percentuale non valida: inserisci un numero da 1 a 50");
                                setTimeout(() => setError(""), 4000);
                                return;
                              }
                              if (!window.confirm(`Inviare un coupon del ${pct}% a ${customer.email}?`)) return;
                              try {
                                const r = await axios.post(
                                  `${API}/admin/customer/send-welcome-coupon`,
                                  { email: customer.email, discount_percent: pct }
                                );
                                if (r.data?.email_sent) {
                                  setSuccess(`✅ Coupon ${r.data.coupon_code} (${pct}%) inviato a ${customer.email}`);
                                } else {
                                  setSuccess(`⚠️ Coupon ${r.data.coupon_code} creato ma email non inviata: ${r.data.email_error || "errore sconosciuto"}`);
                                }
                                setTimeout(() => setSuccess(""), 6000);
                              } catch (err) {
                                setError(err.response?.data?.detail || "Errore invio coupon");
                                setTimeout(() => setError(""), 4000);
                              }
                            }}
                              data-testid={`resend-welcome-coupon-btn-${customer.id}`}
                            >
                              🎁
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                              title="Invia email di reset password a questo cliente"
                              onClick={async () => {
                                if (!window.confirm(`Inviare l'email di reset password a ${customer.email}?\n\nIl cliente riceverà un link (valido 1 ora) per scegliere una nuova password.`)) return;
                                try {
                                  await axios.post(`${API}/admin/customer/send-password-reset`, { email: customer.email });
                                  setSuccess(`🔑 Email di reset password inviata a ${customer.email}`);
                                  setTimeout(() => setSuccess(""), 6000);
                                } catch (err) {
                                  setError(err.response?.data?.detail || "Errore invio reset password");
                                  setTimeout(() => setError(""), 4000);
                                }
                              }}
                              data-testid={`send-password-reset-btn-${customer.id}`}
                            >
                              🔑
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="px-2"
                              title="Elimina cliente"
                              onClick={async () => {
                                if (
                                  window.confirm(
                                    `Eliminare il cliente ${customer.full_name || customer.username}?`
                                  )
                                ) {
                                  try {
                                    await axios.delete(`${API}/customers/${customer.id}`);
                                    fetchCustomers();
                                    setSuccess("Cliente eliminato");
                                    setTimeout(() => setSuccess(""), 3000);
                                  } catch (err) {
                                    setError("Errore eliminazione");
                                    setTimeout(() => setError(""), 3000);
                                  }
                                }
                              }}
                            >
                              🗑️
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Orders Modal */}
      {selectedCustomer && customerOrders !== null && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedCustomer(null);
            setCustomerOrders(null);
          }}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="customer-orders-modal"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                Ordini di {selectedCustomer.full_name || selectedCustomer.username}
              </h3>
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerOrders(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                &times;
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-2">{selectedCustomer.email}</p>
            {/* Full customer data block (May 2026 — admin needs visibility of address/CF) */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-xs space-y-1" data-testid="customer-details-block">
              {selectedCustomer.phone && (
                <div><span className="text-gray-500">Telefono:</span> <span className="text-gray-900">{selectedCustomer.phone}</span></div>
              )}
              {(selectedCustomer.address || selectedCustomer.default_address) && (
                <div>
                  <span className="text-gray-500">Indirizzo:</span>{" "}
                  <span className="text-gray-900">
                    {selectedCustomer.address || selectedCustomer.default_address}
                    {(selectedCustomer.postal_code || selectedCustomer.default_zip) && (
                      <>, {selectedCustomer.postal_code || selectedCustomer.default_zip}</>
                    )}
                    {(selectedCustomer.city || selectedCustomer.default_city) && (
                      <> {selectedCustomer.city || selectedCustomer.default_city}</>
                    )}
                    {(selectedCustomer.country || selectedCustomer.default_country) && (
                      <> ({selectedCustomer.country || selectedCustomer.default_country})</>
                    )}
                  </span>
                </div>
              )}
              {selectedCustomer.codice_fiscale && (
                <div>
                  <span className="text-gray-500">Codice Fiscale:</span>{" "}
                  <span className="text-gray-900 font-mono">{selectedCustomer.codice_fiscale}</span>
                </div>
              )}
              {selectedCustomer.referral_code && (
                <div>
                  <span className="text-gray-500">Codice referral:</span>{" "}
                  <span className="text-gray-900 font-mono">{selectedCustomer.referral_code}</span>
                  {selectedCustomer.referral_count > 0 && (
                    <span className="ml-2 text-purple-600">({selectedCustomer.referral_count} amici invitati)</span>
                  )}
                </div>
              )}
            </div>
            {/* Coupon history (June 2026 — who received what, and who used it) */}
            <div className="mb-4" data-testid="customer-coupons-section">
              <h4 className="text-sm font-bold text-gray-900 mb-2">🎟️ Coupon del cliente</h4>
              {customerCoupons === null ? (
                <p className="text-xs text-gray-400">Caricamento coupon…</p>
              ) : customerCoupons.length === 0 ? (
                <p className="text-xs text-gray-400">Nessun coupon inviato o usato</p>
              ) : (
                <div className="space-y-1.5">
                  {customerCoupons.map((cp) => (
                    <div
                      key={cp.code}
                      className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-xs"
                      data-testid={`customer-coupon-${cp.code}`}
                    >
                      <span className="font-mono font-semibold text-gray-900">{cp.code}</span>
                      <span className="text-gray-600">
                        {cp.free_shipping
                          ? "spedizione gratis"
                          : cp.discount_percent
                            ? `${cp.discount_percent}%`
                            : cp.discount_value_fixed
                              ? `€${cp.discount_value_fixed}`
                              : ""}
                      </span>
                      <span
                        className={`ml-auto px-2 py-0.5 rounded-full font-medium ${
                          cp.status === "usato"
                            ? "bg-green-100 text-green-700"
                            : cp.status === "attivo"
                              ? "bg-blue-100 text-blue-700"
                              : cp.status === "scaduto"
                                ? "bg-gray-200 text-gray-600"
                                : "bg-red-100 text-red-600"
                        }`}
                      >
                        {cp.status}
                      </span>
                      {cp.used_in_order && (
                        <span className="text-gray-500">
                          ordine #{(cp.used_in_order.order_id || "").slice(-6)}
                          {cp.used_in_order.order_date &&
                            ` · ${new Date(cp.used_in_order.order_date).toLocaleDateString("it-IT")}`}
                        </span>
                      )}
                      {!cp.used_in_order && cp.expires_at && cp.status === "attivo" && (
                        <span className="text-gray-400">
                          scade {new Date(cp.expires_at).toLocaleDateString("it-IT")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {customerOrders.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nessun ordine trovato</p>
            ) : (
              <div className="space-y-3">
                {customerOrders.map((order) => (
                  <div key={order.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-mono text-sm text-gray-500">#{order.id?.slice(-6)}</span>
                        <span
                          className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                            order.status === "delivered"
                              ? "bg-green-100 text-green-700"
                              : order.status === "shipped"
                                ? "bg-blue-100 text-blue-700"
                                : order.status === "confirmed"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {order.status}
                        </span>
                      </div>
                      <span className="font-bold text-green-600">{formatPrice(order.total_amount || 0)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(order.order_date).toLocaleDateString("it-IT")} — {order.payment_method} —{" "}
                      {order.items?.length || 0} articoli
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
